import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@lib/cn"

export const badgeVariants = cva(
  [
    "inline-flex items-center gap-1 rounded-sm border font-medium whitespace-nowrap",
    "transition-colors duration-quick motion-reduce:transition-none",
  ].join(" "),
  {
    variants: {
      variant: {
        neutral:
          "bg-cloverhound-gray-100 text-cloverhound-gray-800 border-cloverhound-gray-250 dark:bg-cloverhound-gray-850 dark:text-cloverhound-gray-200 dark:border-cloverhound-gray-800",
        info:
          "bg-cloverhound-blue-50 text-cloverhound-blue-700 border-cloverhound-blue-100 dark:bg-cloverhound-blue-700/20 dark:text-cloverhound-blue-300 dark:border-cloverhound-blue-700/40",
        success:
          "bg-cloverhound-green-50 text-cloverhound-green-700 border-cloverhound-green-100 dark:bg-cloverhound-green-500/20 dark:text-cloverhound-green-300 dark:border-cloverhound-green-500/40",
        warning:
          "bg-cloverhound-yellow-50 text-cloverhound-yellow-600 border-cloverhound-yellow-100 dark:bg-cloverhound-yellow-500/15 dark:text-cloverhound-yellow-400 dark:border-cloverhound-yellow-500/40",
        danger:
          "bg-cloverhound-red-50 text-cloverhound-red-700 border-cloverhound-red-500/20 dark:bg-cloverhound-red-500/20 dark:text-cloverhound-red-500 dark:border-cloverhound-red-500/40",
        accent:
          "bg-cloverhound-indigo-50 text-cloverhound-indigo-700 border-cloverhound-indigo-100 dark:bg-cloverhound-indigo-500/15 dark:text-cloverhound-indigo-300 dark:border-cloverhound-indigo-500/30",
        outline:
          "bg-transparent text-cloverhound-gray-800 border-cloverhound-gray-250 dark:text-cloverhound-gray-300 dark:border-cloverhound-gray-800",
      },
      size: {
        sm: "h-5 px-1.5 text-micro",
        md: "h-6 px-2 text-mini",
      },
      shape: {
        rect: "rounded-sm",
        pill: "rounded-pill px-2.5",
      },
    },
    defaultVariants: { variant: "neutral", size: "md", shape: "rect" },
  },
)

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {
  icon?: React.ReactNode
}

export const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, variant, size, shape, icon, children, ...props }, ref) => (
    <span ref={ref} className={cn(badgeVariants({ variant, size, shape }), className)} {...props}>
      {icon && <span className="[&>*]:size-3" aria-hidden="true">{icon}</span>}
      {children}
    </span>
  ),
)
Badge.displayName = "Badge"
